import React, {
  memo,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { connect } from 'react-redux';

import {
  activate as activateAction,
  deactivate as deactivateAction,
  setHeadline as setHeadlineAction,
  setShortcutVisible as setShortcutsVisibleAction,
  setTheme as setThemeAction,
  setDuration as setDurationAction,
} from 'redux/modules/navbar';

import BTE from 'lib/BTE';

export const NAVBAR_THEME = {
  TRANSPARENT: 'transparent',
  TRANSPARENT_LIGHT: 'transparentLight',
  LIGHT: 'light',
  DARK: 'dark',
  VERTICAL: 'vertical',
};

/**
 * Sets the headline in the navbar using the provided getHeadline function.
 *
 * @param {function({ content: object }): string} getHeadline - Function to generate the headline.
 * @param {any} navbarContent - Content to pass to getHeadline.
 * @param {function(string): void} navbarSetHeadline - Function to update the headline state.
 */
const setHeadline = (getHeadline, navbarContent, navbarSetHeadline) => {
  if (!getHeadline) return;

  const headline = getHeadline({
    content: navbarContent,
  });

  navbarSetHeadline(headline);
};

/**
 * Sets the visibility of shortcuts in the navbar using the provided getShowShortcuts function.
 *
 * @param {function({ content: object }): boolean} getShowShortcuts - Function to determine which shortcuts to show.
 * @param {any} navbarContent - Content to pass to getShowShortcuts.
 * @param {function(boolean): void} navbarSetShortcutVisible - Function to update shortcut visibility state.
 */
const setShowShortcuts = (getShowShortcuts, navbarContent, navbarSetShortcutVisible) => {
  if (!getShowShortcuts) return;

  const shortcuts = getShowShortcuts({
    content: navbarContent,
  });

  navbarSetShortcutVisible(shortcuts);
};

/**
 * Sets the theme for the navbar using the provided getTheme function.
 *
 * @param {function({ content: object, path: string, section: string; vertical: string }): string} getTheme - Function to generate the theme object.
 * @param {object} navbarContent - Content to pass to getTheme.
 * @param {string} path - Current navigation path.
 * @param {string} section - Current section in the navbar.
 * @param {boolean} vertical - Whether the navbar is vertical.
 * @param {(string) => void} navbarSetTheme - Function to update the theme state.
 */
const setTheme = (getTheme, navbarContent, path, section, vertical, navbarSetTheme) => {
  if (!getTheme) return;

  const theme = getTheme({
    content: navbarContent,
    path,
    section,
    vertical,
  });

  navbarSetTheme(theme);
};

/**
 * Sets the animation duration for the navbar if the provided duration is valid.
 *
 * @param {number} localAnimationDuration - The desired animation duration.
 * @param {Function} navbarSetDuration - Function to update the animation duration state.
 */
const setDuration = (localAnimationDuration, navbarSetDuration) => {
  if (localAnimationDuration <= -1) return;

  navbarSetDuration(localAnimationDuration);
};

/**
 * @param {object} props - The props for the Navbar component.
 * @param {React.ReactNode} props.children - The child components to render inside the Navbar
 * @param {React.FunctionComponent} props.Page - The Page component to render with the Navbar.
 * @param {number | function({ vertical: string, navbarContent: object }): number} props.getActiveAt - Function to determine the scroll position at which the navbar becomes active.
 * @param {function({ content: object }): string} props.getHeadline - Function to determine
 * @param {function({ content: object }): boolean} props.getShowShortcuts - Function to determine if navigation shortcuts are shown.
 * @param {function({ content: object, path: string, section: string; vertical: string }): string} props.getTheme - Function to determine the theme value.
 * @param {number} props.localAnimationDuration - Duration in seconds to override local animations.
 * @param {object} props.props - Additional props to pass to the Page component.
 */
const Navbar = ({
  children,
  Page,
  getActiveAt,
  getHeadline,
  getShowShortcuts,
  getTheme,
  localAnimationDuration,
  ...props
}) => {
  const mounted = useRef(false);
  const {
    navbarActive = false,
    navbarActivate,
    navbarContent,
    navbarDeactivate,
    navbarSetHeadline,
    navbarSetShortcutVisible,
    navbarSetTheme,
    navbarSetDuration,
    path,
    section,
    vertical,
  } = props;

  const childProps = useMemo(() => Object.keys(props)
    .filter((key) => key.search(/^navbar[A-Z]/) === -1)
    .reduce((obj, key) => {
      // eslint-disable-next-line no-param-reassign
      obj[key] = props?.[key];
      return obj;
    }, {}), [props]);


  useEffect(() => {
    mounted.current = true;
  }, []);

  if (!mounted.current) {
    setHeadline(getHeadline, navbarContent, navbarSetHeadline);
    setShowShortcuts(getShowShortcuts, navbarContent, navbarSetShortcutVisible);
    setTheme(getTheme, navbarContent, path, section, vertical, navbarSetTheme);
    setDuration(localAnimationDuration, navbarSetDuration);
  }

  useEffect(() => {
    /**
      * Monitor the scroll position of the page to determine when to activate the navbar.
      *
      * If the vertical is 'today', do not monitor the scroll position.
      *
      * If a getActiveAt function is provided, call the function with the vertical and navbarContent
      * props and use the result as the activeAt value. If the result is not null, monitor the scroll
      * position and activate the navbar when the page is scrolled to the activeAt position.
      *
      * @see getActiveAt
      */

    if (!getActiveAt) return undefined;

    const activeAt = typeof getActiveAt === 'function' ? getActiveAt({ vertical, navbarContent }) : getActiveAt;

    if (!activeAt) {
      return undefined;
    }

    /**
      * Event handler for the 'scroll' event, throttled to 30 milliseconds.
      * Checks if the current scroll depth is greater than the `activeAt` value.
      * If it is, and the navbar is not currently active, it activates the navbar.
      * If it is not, and the navbar is currently active, it deactivates the navbar.
      *
      * @param {number} depth - the current scroll depth
      */
    const monitorScroll = (depth) => {
      if (depth > activeAt && !navbarActive) {
        navbarActivate();
        return;
      }

      if (depth < activeAt && navbarActive) {
        navbarDeactivate();
      }
    };

    BTE.on('scroll', monitorScroll, 'throttle');

    return () => {
      BTE.remove('scroll', monitorScroll, 'throttle');
    };
  }, [navbarActive, getActiveAt, vertical, navbarContent, navbarActivate, navbarDeactivate]);

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Page data-test="nav-bar-page" {...childProps}>
      {children}
    </Page>
  );
};

/**
  * Connects store props to the Navbar component.
  * @param {object} storeProps Redux store state.
  * @param {object} ownProps Navbar props.
  * @property {boolean} navbarActive Whether the navbar is active.
  * @property {object} navbarContent The content of the navbar.
  * @property {string} pageView The current page view.
  * @property {string} path The current path.
  * @property {string} section The current section.
  * @property {string} vertical The current vertical.
  * @returns {object} Props to pass to the Navbar component.
  */
const mapStateToProps = (storeProps, ownProps) => {
  const {
    navbar: { active },
    article,
    front,
    recipe,
    slideshow,
    video,
  } = storeProps;

  const {
    pageView,
    path,
    section,
    vertical,
  } = ownProps;

  let navbarContent = {};
  switch (pageView) {
    case 'article':
    case 'showBlog':
      navbarContent = article?.content?.[0] ?? {};
      break;
    case 'front':
      navbarContent = front;
      break;
    case 'recipe':
      navbarContent = recipe?.current ?? {};
      break;
    case 'slideshow':
      navbarContent = slideshow?.current ?? {};
      break;
    case 'video':
      navbarContent = video;
      break;
    default:
      navbarContent = {};
  }

  return {
    // store props
    navbarActive: active,
    navbarContent,
    // own props
    pageView,
    path,
    section,
    vertical,
  };
};

/**
  * Maps dispatch to props for the Navbar component, allowing the component
  * to trigger various actions related to the navbar state. Each function
  * wraps an action creator and dispatches it, enabling the component
  * to activate or deactivate the navbar, set the visibility of shortcuts,
  * update the headline, set the theme, and adjust the duration of animations.
  *
  * @param {Function} dispatch - Redux dispatch function to send actions to the store.
  * @returns {object} An object containing functions that dispatch actions to update
  * the navbar state.
  */
const mapActionsToProps = (dispatch) => ({
  /**
    * activates navbar
    */
  navbarActivate: () => dispatch(activateAction()),
  /**
    * Deactivate the navbar.
    */
  navbarDeactivate: () => dispatch(deactivateAction()),
  /**
    * Dispatches an action to set the visibility of shortcuts in the navbar state.
    * @param {boolean} payload - the new visibility value
    */
  navbarSetShortcutVisible: (payload) => dispatch(setShortcutsVisibleAction(payload)),
  /**
    * Dispatches an action to set the headline value in the navbar state.
    * @param {string} payload - the new headline value
    */
  navbarSetHeadline: (payload) => dispatch(setHeadlineAction(payload)),
  /**
    * Dispatches an action to set the theme value in the navbar state.
    * @param {string} payload - the new headline value
    */
  navbarSetTheme: (payload) => dispatch(setThemeAction(payload)),
  /**
    * Dispatches an action to set the duration of navbar visibility
    * @param {string} payload
    */
  navbarSetDuration: (payload) => dispatch(setDurationAction(payload)),
});

const ConnectedNavbar = connect(mapStateToProps, mapActionsToProps)(memo(Navbar, () => false));

/**
 * Higher-order component for configuring the navigation bar.
 *
 * @param {object} options - Configuration options.
 * @param {number | function({ vertical: string, navbarContent: object }): number} options.activeAt - ScrollY position or function to trigger the 'active' menu class.
 * @param {number} options.setDuration - Duration in seconds to override local animations.
 * @param {function({ content: object }): string} options.headline - Function to determine the content headline.
 * @param {function({ content: object }): boolean} options.showShortcuts - Function to determine if navigation shortcuts are shown. Navigation shortcuts are enabled by default
 * @param {function({ content: object, path: string, section: string; vertical: string }): string} options.theme - Function to determine the theme value.
 * @returns {function(React.FunctionComponent): React.Element} - Returns a function that takes a Page component.
 */
export const navbar = ({
  activeAt: getActiveAt,
  setDuration: localAnimationDuration,
  headline: getHeadline,
  showShortcuts: getShowShortcuts,
  theme: getTheme,
}) => (Page) => {
  /**
   * Renders the ConnectedNavbar component with injected configuration and props.
   *
   * @param {object} props - Props to pass to ConnectedNavbar and the wrapped Page.
   */
  const PageWithConnectedNavbar = (props) => (
    <ConnectedNavbar
      getActiveAt={getActiveAt}
      getHeadline={getHeadline}
      getShowShortcuts={getShowShortcuts}
      getTheme={getTheme}
      localAnimationDuration={localAnimationDuration}
      Page={Page}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );

  PageWithConnectedNavbar.getInitialProps = Page.getInitialProps;

  return PageWithConnectedNavbar;
};
